import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import {
  purchaseListingFreeTier,
  payListingWithWallet,
} from "@/lib/listingDuration";
import CouponApplyField from "@/components/checkout/CouponApplyField";
import { WalletPayPanel } from "@/components/vendor/WalletPayPanel";
import { useWalletBalance } from "@/hooks/useWalletBalance";
import { api } from "@/lib/api";

/**
 * RenewListingDialog — Phase 18 Round 2B cutover.
 *
 * Vendor picks a fresh listing duration (7d Free / 15d ₹99 / 30d ₹149).
 * The 7d free tier stays as a plain click-to-list (no wallet). The
 * paid tiers now debit the vendor's wallet via /listing/pay-with-wallet
 * and render the shared WalletPayPanel so insufficient balance is a
 * first-class UI state with a top-up CTA.
 *
 * Backend still handles renewal semantics (extends from current
 * expiry when still in the future, else from now).
 *
 * Coupons apply on paid tiers only — the 7d Free tier already costs
 * nothing so the field is disabled there.
 */
const NET_PRICE_BY_DURATION = { 7: 0, 15: 99, 30: 149 };

export default function RenewListingDialog({ open, onOpenChange, campaign, onSuccess }) {
  const [duration, setDuration] = useState(15);
  const [busy, setBusy] = useState(false);
  const [couponApplied, setCouponApplied] = useState(null);
  // Only fetch the wallet balance while the dialog is open — no need
  // to keep it warm otherwise.
  const wallet = useWalletBalance({ autoLoad: open });

  const netPrice = NET_PRICE_BY_DURATION[duration] ?? 0;
  const isPaid = netPrice > 0;

  const finalNet = useMemo(() => {
    if (!couponApplied || !isPaid) return netPrice;
    return Math.max(0, netPrice - Number(couponApplied.discount || 0));
  }, [couponApplied, netPrice, isPaid]);
  const finalGross = useMemo(() => Number((finalNet * 1.18).toFixed(2)), [finalNet]);
  const finalGrossPaise = Math.round(finalGross * 100);

  const submit = async () => {
    if (busy || !campaign?.id) return;
    setBusy(true);
    try {
      if (!isPaid) {
        // 7d free tier — no wallet debit.
        await purchaseListingFreeTier({
          campaignId: campaign.id,
          isRenewal: true,
        });
      } else {
        await payListingWithWallet({
          campaignId: campaign.id,
          durationDays: duration,
          couponCode: couponApplied?.code || null,
          isRenewal: true,
        });
      }
      onOpenChange?.(false);
      onSuccess?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      if (status === 402 && detail?.code === "insufficient_wallet_balance") {
        // Update the balance widget with the server-echoed value so
        // the "short by" state below the coupon block flips on
        // immediately without a follow-up round-trip.
        if (Number.isFinite(Number(detail.balance_paise))) {
          wallet.setBalancePaise(Number(detail.balance_paise));
        }
        toast.error(detail.message || "Not enough wallet balance.");
      } else if (typeof detail === "object" && detail?.message) {
        toast.error(detail.message);
      } else {
        toast.error(detail || e?.message || "Renewal failed");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="renew-listing-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-[#F5A800]" /> Renew listing
          </DialogTitle>
          <DialogDescription>
            Keep &quot;{campaign?.name || "this offer"}&quot; visible on Explore. Renewals extend from your current expiry — no lost days.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-2 py-2">
          {[
            { d: 7, label: "7 days", price: "Free" },
            { d: 15, label: "15 days", price: "₹99 + GST" },
            { d: 30, label: "30 days", price: "₹149 + GST" },
          ].map((opt) => {
            const active = duration === opt.d;
            return (
              <button
                key={opt.d}
                type="button"
                onClick={() => {
                  setDuration(opt.d);
                  // Duration change may push out of paid → clear coupon
                  if (couponApplied) setCouponApplied(null);
                }}
                className={`rounded-lg border-2 p-3 text-center transition-colors ${active ? "border-[#F5A800] bg-[#F5A800]/15" : "border-border hover:border-[#F5A800]/60"}`}
                data-testid={`renew-opt-${opt.d}`}
              >
                <div className="font-heading text-lg font-bold">{opt.label}</div>
                <div className="text-xs text-muted-foreground">{opt.price}</div>
              </button>
            );
          })}
        </div>

        {isPaid && (
          <div className="border-t border-border pt-3 space-y-3" data-testid="renew-coupon-block">
            <CouponApplyField
              testIdPrefix="renew-coupon"
              applied={couponApplied}
              disabled={busy}
              onRemove={() => setCouponApplied(null)}
              onApply={async (code) => {
                const r = await api.previewBillingCoupon({
                  code,
                  base_amount: netPrice,
                  surface: "listing_duration",
                  // Round 2B — pass checkout_city so franchise-scoped
                  // coupons validate correctly. Backend's pay-with-wallet
                  // endpoint uses the same fallback (campaign_city →
                  // vendor.city) so the preview + confirm are consistent.
                  checkout_city: campaign?.campaign_city || null,
                });
                setCouponApplied(r.data.billing_coupon);
                toast.success(
                  `Coupon ${r.data.billing_coupon.code} applied — you save ₹${Number(r.data.billing_coupon.discount).toFixed(2)}`,
                );
              }}
            />
            {couponApplied && (
              <p className="text-[11px] text-muted-foreground" data-testid="renew-total-hint">
                Total after coupon: <b>₹{finalGross.toFixed(2)}</b> (incl. 18% GST on ₹{finalNet.toFixed(2)})
              </p>
            )}
            {/* Wallet balance widget only on paid tiers — the free
                7d tier doesn't touch the wallet, so surfacing balance
                info would be noise. */}
            <WalletPayPanel
              loading={wallet.loading}
              error={wallet.error}
              balancePaise={wallet.balancePaise}
              promoPaise={wallet.promoPaise}
              requiredPaise={finalGrossPaise}
              busy={busy}
              onPay={submit}
              onRefresh={wallet.refresh}
              payLabel={`Pay & renew ${duration}d · ₹${finalGross.toFixed(2)}`}
              compact
              testIdPrefix="renew-wallet"
            />
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange?.(false)} data-testid="renew-cancel">Cancel</Button>
          {/* 7d free tier still needs its own confirm button; the
              WalletPayPanel takes over the Pay button on paid tiers. */}
          {!isPaid && (
            <Button
              onClick={submit}
              disabled={busy}
              className="bg-[#F5A800] text-[#0A0A0A] hover:bg-[#F5A800]/90"
              data-testid="renew-confirm"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Renew for 7 days
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
